import { Link } from "react-router-dom";
import { Check, Hand, ArrowRightLeft, Award, MousePointerClick } from "lucide-react";
import Seo from "@/components/Seo";
import ArtworkZoom from "@/components/ArtworkZoom";
import ProductPlaceholder from "@/components/ProductPlaceholder";
import BuyButton from "@/components/BuyButton";
import BuyOrNotify from "@/components/BuyOrNotify";
import { IMAGES, WAYS_TO_PLAY, HOW_IT_WORKS, SENIORS_AUDIENCE, PRICES, EXTRA_BUCKS, SENIORS_DECK_IMAGE, SENIORS_KIT_IMAGE, SENIORS_IN_USE_IMAGE, BUCKS_IMAGE } from "@/data/content";

const WAY_ICONS = [MousePointerClick, Hand, ArrowRightLeft, Award];

const Seniors = () => {
  return (
    <div data-testid="seniors-page">
      <Seo
        title="UNSTUCK Seniors — Inclusive 15-Minute Activity Cards for Senior Living"
        description="Simple senior activity cards for assisted living, nursing homes, memory care, and independent living. Inclusive activities for seniors of all abilities, including limited mobility or speech."
        path="/seniors"
      />
      {/* WHO IT'S FOR */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="animate-fade-up">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-5">
              The featured UNSTUCK collection
            </p>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-ink mb-6">
              UNSTUCK Seniors
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed text-ink/80 mb-8">
              Simple 15-minute activity cards that help older adults take one small step, stay engaged,
              connect, participate, and enjoy meaningful moments throughout the day.
            </p>
            <h2 className="font-heading font-bold text-2xl text-ink mb-4">Designed for</h2>
            <ul className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="seniors-audience-list">
              {SENIORS_AUDIENCE.map((item) => (
                <li key={item} className="flex items-start gap-2 text-base text-ink/85">
                  <Check className="h-5 w-5 text-sage shrink-0 mt-0.5" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="bg-white border-l-4 border-gold rounded-r-2xl p-6">
              <p className="text-lg leading-relaxed text-ink/85">
                <span className="font-semibold text-ink">Individual-first.</span> A senior does not have
                to participate in a group to use UNSTUCK. The deck can be used alone, with a caregiver or
                family member, with another resident, or in a group setting.
              </p>
            </div>
          </div>
          <div className="grid gap-6">
            <img
              src={IMAGES.caregiver}
              alt="A caregiver smiling with an older adult in a warm home setting"
              data-testid="seniors-caregiver-image"
              className="w-full aspect-[4/3] object-cover rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)]"
              loading="eager"
            />
            <ProductPlaceholder label="UNSTUCK Seniors full kit" ratio="aspect-[16/9]" testId="seniors-kit-placeholder" src={SENIORS_KIT_IMAGE} alt="Complete UNSTUCK Seniors kit: four decks, instruction guides, and UNSTUCK Bucks" />
          </div>
        </div>
      </section>

      {/* FREE TRIAL BAND */}
      <section className="bg-gold-light border-y border-gold/30">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <div className="max-w-2xl">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink mb-3">
              Try UNSTUCK free for 14 days
            </h2>
            <p className="text-lg leading-relaxed text-ink/80">
              Experience 14 complimentary UNSTUCK Seniors activity cards, created by an activity director
              to help residents of different abilities connect, participate, and enjoy meaningful
              15-minute activities. No payment needed.
            </p>
          </div>
          <Link
            to="/try-free"
            data-testid="seniors-free-trial-button"
            className="inline-flex shrink-0 items-center justify-center gap-2 bg-sage text-white hover:bg-sage-dark rounded-full px-10 py-5 text-xl font-semibold transition-all duration-300 shadow-[0_2px_10px_rgba(74,93,78,0.2)] focus:outline-none focus:ring-2 focus:ring-sage focus:ring-offset-2"
          >
            Try UNSTUCK Free for 14 Days
          </Link>
        </div>
      </section>

      {/* HOW THE CARDS WORK */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24">
          <div className="max-w-3xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">How the cards work</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink mb-5">
              Four easy steps. No rules to memorize.
            </h2>
            <p className="text-lg leading-relaxed text-ink/80">
              The activities are intentionally simple because the goal is to help someone start. A small
              action can create momentum for the rest of the day.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="how-it-works-grid">
            {HOW_IT_WORKS.map((step) => (
              <div
                key={step.step}
                className="bg-cream rounded-2xl p-8 border border-border/60 shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="h-12 w-12 rounded-full bg-sage text-white font-heading font-bold text-xl flex items-center justify-center mb-5">
                  {step.step}
                </div>
                <h3 className="font-heading font-bold text-xl text-ink mb-2">{step.title}</h3>
                <p className="text-base leading-relaxed text-ink/75">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 WAYS TO PLAY */}
      <section className="bg-sage-light">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24">
          <div className="max-w-3xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">4 Ways to Play</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink mb-5">
              PULL IT • PASS IT • TRADE IT • EARN IT
            </h2>
            <p className="text-lg leading-relaxed text-ink/80">
              UNSTUCK can be used solo, caregiver-assisted, partner-to-partner, or in groups.
            </p>
          </div>
          <div className="max-w-3xl mx-auto mb-14">
            <ArtworkZoom
              src="/images/four-ways-artwork.jpg"
              alt="Official UNSTUCK Seniors artwork: How to Play and the 4 Ways to Play — Solo Reset, Caregiver Assist, Group Play, and Bucks + Rewards"
              testId="four-ways-artwork"
            />
            <p className="text-base text-ink/60 mt-3 text-center">
              The official UNSTUCK Seniors guide — exactly as it comes in the kit.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-14" data-testid="ways-to-play-grid">
            {WAYS_TO_PLAY.map((way, i) => {
              const Icon = WAY_ICONS[i];
              return (
                <div
                  key={way.name}
                  className="bg-white rounded-2xl p-8 border border-border/60 shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-transform duration-300 hover:-translate-y-1 flex gap-5 items-start"
                >
                  <div className="h-12 w-12 shrink-0 rounded-full bg-gold-light flex items-center justify-center">
                    <Icon className="h-6 w-6 text-gold-dark" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-2xl text-sage mb-2">{way.name}</h3>
                    <p className="text-base leading-relaxed text-ink/75">{way.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <img
              src={IMAGES.group}
              alt="Two older men playing a game together"
              data-testid="seniors-group-image"
              className="w-full aspect-[16/9] object-cover rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)]"
              loading="lazy"
            />
            <img
              src={IMAGES.independent}
              alt="An older woman reading comfortably at home"
              data-testid="seniors-independent-image"
              className="w-full aspect-[16/9] object-cover rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)]"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* UNSTUCK BUCKS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24">
          <div className="max-w-3xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">UNSTUCK Bucks</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink mb-5">
              An optional way to celebrate every small step.
            </h2>
            <p className="text-lg leading-relaxed text-ink/80 mb-4">
              UNSTUCK Bucks are an optional motivational reward system — they are{" "}
              <span className="font-semibold text-ink">not real money</span>. Organizations or families
              choose their own rewards, and using the reward system is never mandatory.
            </p>
          </div>
          <div className="max-w-2xl mx-auto mb-14">
            <ArtworkZoom
              src={BUCKS_IMAGE}
              alt="Official UNSTUCK Bucks designs: $1 Showed Up, $5 Keep Going, $10 Great Day, $20 Big Reset — game use only, no cash value"
              testId="bucks-artwork"
            />
            <p className="text-base text-ink/60 mt-3 text-center">
              The real UNSTUCK Bucks — exactly as they come in the kit.
            </p>
          </div>
          {/* EXTRA UNSTUCK BUCKS — separate purchasable item */}
          <div className="bg-cream rounded-2xl border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-8 sm:p-10 grid gap-8 lg:grid-cols-2 lg:items-center" data-testid="extra-bucks-section">
            <div>
              <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-3">Digital printable PDF — sold separately</p>
              <h3 className="font-heading font-bold text-2xl sm:text-3xl text-ink mb-4">{EXTRA_BUCKS.name}</h3>
              <p className="text-lg leading-relaxed text-ink/80 mb-4">
                A digital printable PDF add-on for the optional UNSTUCK Bucks reward system. Print as many
                as you need for personal or internal program use. Delivered digitally right after purchase —
                nothing ships.
              </p>
              <p className="text-base leading-relaxed text-ink/70 mb-4">
                The Complete UNSTUCK Seniors Program already includes the Bucks PDF. This separate download
                is for customers who want extra access to the Bucks system, or who purchased another deck
                and want to add the reward system.
              </p>
              <p className="text-sm uppercase tracking-widest font-semibold text-ink/60 mb-2">
                Game use only • No cash value
              </p>
              <p className="text-sm text-ink/60 mb-6">
                Personal or internal program use only. No resale or redistribution.
              </p>
              <p className="font-heading font-black text-3xl text-sage mb-6" data-testid="extra-bucks-price">
                ${EXTRA_BUCKS.price.toFixed(2)} <span className="text-lg font-body font-medium text-ink/60">digital download</span>
              </p>
              <BuyOrNotify
                lookupKey={EXTRA_BUCKS.lookupKey}
                price={EXTRA_BUCKS.price}
                label={`Buy Extra UNSTUCK Bucks — $${EXTRA_BUCKS.price.toFixed(2)}`}
                testId="buy-extra-bucks-button"
                className="bg-gold text-ink hover:bg-gold-dark hover:text-white"
              />
            </div>
            <ProductPlaceholder label="Extra UNSTUCK Bucks" ratio="aspect-[4/3]" testId="extra-bucks-placeholder" src={BUCKS_IMAGE} alt="Sheet of printable UNSTUCK Bucks in $1, $5, $10 and $20 designs" />
          </div>
        </div>
      </section>

      {/* PURCHASE OPTIONS */}
      <section id="program" className="bg-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24">
          <div className="max-w-3xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">Two ways to buy</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink mb-5">
              Choose the Seniors option that fits.
            </h2>
            <p className="text-lg leading-relaxed text-ink/80">
              The full program and the individual deck are two separate buying options — start with the
              complete program, or begin with a single deck.
            </p>
          </div>
          <div className="grid gap-8 lg:grid-cols-2" data-testid="purchase-options">
            {/* Full program */}
            <div className="relative bg-white rounded-2xl p-8 sm:p-10 border-2 border-sage shadow-[0_4px_20px_rgba(0,0,0,0.05)] flex flex-col">
              <span className="absolute -top-4 left-8 bg-gold text-ink text-sm font-semibold uppercase tracking-widest rounded-full px-4 py-1.5">
                Main offer
              </span>
              <h3 className="font-heading font-bold text-2xl sm:text-3xl text-ink mb-2 mt-2">
                Complete UNSTUCK Seniors Program
              </h3>
              <p className="font-heading font-black text-4xl text-sage mb-6" data-testid="program-price">
                ${PRICES.seniorsProgram.toFixed(0)} <span className="text-lg font-body font-medium text-ink/60">+ shipping</span>
              </p>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  "Four individually packaged 54-card UNSTUCK Seniors decks",
                  "Printed instructions",
                  "UNSTUCK Bucks reward set",
                  "Printable PDF support materials: instructions, Bucks, Participation & Progress Tracker, and Tracker guide",
                  "Program packaging",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-ink/85">
                    <Check className="h-5 w-5 text-sage shrink-0 mt-0.5" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <ProductPlaceholder label="Complete Seniors Program packaging" ratio="aspect-[16/9]" testId="program-packaging-placeholder" src={SENIORS_KIT_IMAGE} alt="Everything in the Complete UNSTUCK Seniors Program: four decks, guides, and Bucks" />
              <div className="mt-8">
                <BuyButton
                  lookupKey="seniors_program"
                  testId="buy-full-program-button"
                  className="w-full bg-sage text-white hover:bg-sage-dark shadow-[0_2px_10px_rgba(74,93,78,0.2)]"
                >
                  Shop Full Seniors Program
                </BuyButton>
              </div>
            </div>
            {/* Individual deck */}
            <div className="bg-white rounded-2xl p-8 sm:p-10 border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] flex flex-col">
              <h3 className="font-heading font-bold text-2xl sm:text-3xl text-ink mb-2 mt-2">
                Individual UNSTUCK Seniors Deck
              </h3>
              <p className="font-heading font-black text-4xl text-sage mb-6" data-testid="deck-price">
                ${PRICES.seniorsDeck.toFixed(2)}
              </p>
              <p className="text-base leading-relaxed text-ink/80 mb-8 flex-1">
                One 54-card UNSTUCK Seniors deck. Ideal if you only want one deck, want to start smaller,
                or are looking for a thoughtful gift.
              </p>
              <ProductPlaceholder label="Individual Seniors deck" ratio="aspect-[16/9]" testId="deck-placeholder" src={SENIORS_DECK_IMAGE} alt="Individual UNSTUCK Seniors deck box with activity cards" />
              <div className="mt-8">
                <BuyButton
                  lookupKey="seniors_deck"
                  testId="buy-individual-deck-button"
                  className="w-full border-2 border-sage bg-transparent text-sage hover:bg-sage hover:text-white"
                >
                  Shop Individual Deck — ${PRICES.seniorsDeck.toFixed(2)}
                </BuyButton>
              </div>
            </div>
          </div>
          <p className="mt-8 text-base text-ink/70 max-w-3xl">
            The printable PDFs included with the Seniors program are support materials for the instructions
            and UNSTUCK Bucks only — delivered digitally after purchase. The physical activity cards
            themselves are not printable PDFs. Physical orders ship within 3–5 business days for a flat
            $12.00 per order.
          </p>
        </div>
      </section>

      {/* PRODUCT PHOTO PLACEHOLDERS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24">
          <div className="max-w-3xl mb-12">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">Real photos coming soon</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink">
              The product, exactly as it is.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <ProductPlaceholder label="Card close-up" ratio="aspect-square" testId="placeholder-card-closeup" src={SENIORS_DECK_IMAGE} alt="UNSTUCK Seniors activity cards fanned in front of the deck box" />
            <ProductPlaceholder label="Packaging detail" ratio="aspect-square" testId="placeholder-packaging" src={SENIORS_KIT_IMAGE} alt="UNSTUCK Seniors program contents laid out: decks, guides, and Bucks" />
            <ProductPlaceholder label="Someone using the cards" ratio="aspect-square" testId="placeholder-in-use" src={SENIORS_IN_USE_IMAGE} alt="A smiling older woman holding UNSTUCK Seniors cards at home" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Seniors;
